import { Megaphone, ShoppingBag } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '@/components/ui/section-header';
import { SoftCard } from '@/components/ui/soft-card';
import { formatCurrency } from '@/lib/currency';

const formatAmount = (amount, currency) => (amount == null ? null : formatCurrency(amount, currency));

/**
 * Add-ons that bill on top of the base plan — Local Ads (daily ad spend) and
 * any enabled channels sourced from `billing.entitlements.channels` cross-
 * referenced with the retailer's live channels list.
 *
 * A channel appears here iff it is `status: 'enabled'` in channels-{stage}
 * AND has a matrix entry (`always-on` core channels — google/storefront —
 * never appear here since they aren't billable).
 *
 * Bundle siblings (product-reservations + local-checkout share `commercetools`;
 * meta-commerce + meta-commerce-feed share `meta`) dedupe to a single row —
 * `preferredBundleDisplay` picks a joined label ("Reservations & Local
 * Checkout") when both are enabled, else the single humanised type.
 *
 * Each row shows the Stripe item the retailer is ACTUALLY billed for
 * (`currentSubItem`). The channel reconcile writes that item off the channel
 * row a moment after a toggle; until it lands the entitlements block flags the
 * channel `inSync: false` and the row reads "Syncing", with the planned state
 * (`priceVariant`) in the copy so the retailer knows what is coming.
 */
export function AddOnsSection({ billing, channels, localAdsBudget, localAdsCurrency }) {
	const items = buildAddOnItems(billing, channels, localAdsBudget, localAdsCurrency);

	return (
		<section>
			<SectionHeader
				eyebrow="Add-ons"
				title="What else you're paying for"
				sub="Channels and Local Ads billed alongside your base plan."
			/>
			<SoftCard className="!p-5 border-border">
				{items.length === 0 ? (
					<div className="rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
						No add-ons yet — enable a paid channel or set a Local Ads budget to see it here.
					</div>
				) : (
					<ul className="divide-y divide-border">
						{items.map((item) => (
							<AddOnRow key={item.id} item={item} />
						))}
					</ul>
				)}
			</SoftCard>
		</section>
	);
}

function buildAddOnItems(billing, channels, localAdsBudget, localAdsCurrency) {
	const items = [];

	if (localAdsBudget && localAdsBudget > 0) {
		items.push({
			id: 'local-ads',
			icon: Megaphone,
			title: 'Local Ads',
			description: `${formatAmount(localAdsBudget, localAdsCurrency) ?? `${localAdsBudget}`} per day, billed daily as you spend.`,
			right: <Badge variant="action">Live</Badge>
		});
	}

	const entitlementsByType = billing?.entitlements?.channels ?? {};
	const enabledByType = new Set((channels ?? []).filter((c) => c?.status === 'enabled').map((c) => c.type));
	const seenBundles = new Set();

	for (const [type, decision] of Object.entries(entitlementsByType)) {
		if (!enabledByType.has(type)) continue;
		if (decision.policy === 'always-on') continue;

		// One row per lookupToken bundle: PR + LC share `commercetools`; meta-commerce +
		// meta-commerce-feed share `meta`. Enabling both siblings only produces one
		// Stripe item and should render as one row.
		const bundleKey = decision.lookupToken ?? `single:${type}`;
		if (seenBundles.has(bundleKey)) continue;
		seenBundles.add(bundleKey);

		items.push(channelAddOn(type, decision, enabledByType));
	}

	return items;
}

function channelAddOn(type, decision, enabledByType) {
	const bundlePeers = (decision.bundledWith ?? []).filter((sibling) => enabledByType.has(sibling));
	const displayType = bundlePeers.length ? preferredBundleDisplay(type, bundlePeers) : type;
	const title = humaniseChannel(displayType);
	const price = decision.prices?.default;

	if (decision.policy === 'plan-grant') {
		return {
			id: `channel-${type}`,
			icon: ShoppingBag,
			title,
			description: bundlePeers.length
				? `Included with your plan · bundled with ${bundlePeers.map(humaniseChannel).join(', ')}.`
				: 'Included with your plan — no extra charge.',
			right: <Badge variant="secondary">Included</Badge>
		};
	}

	// The reconcile has not landed this channel's Stripe item yet (or the item
	// still reflects a previous state). Say what the planner intends and mark it
	// syncing rather than describing an item that is about to change.
	if (decision.inSync === false) {
		const planned =
			decision.priceVariant === 'included'
				? 'Free with Pro — one of your allowance slots'
				: price
					? `${formatAmount(price.amount / 100, price.currency)} per month, per location`
					: 'Billed alongside your base plan';
		return {
			id: `channel-${type}`,
			icon: ShoppingBag,
			title,
			description: `${planned}. Updating your subscription…`,
			right: <Badge variant="secondary">Syncing</Badge>
		};
	}

	// The row reflects the *actual* Stripe item state (`currentSubItem.priceVariant`),
	// not the hypothetical price for the next enable (`decision.priceVariant`).
	// `currentSubItem.priceVariant === 'included'` = channel is in a free Pro
	// allowance slot; anything else = paid at the default rate.
	const currentVariant = decision.currentSubItem?.priceVariant;

	if (currentVariant === 'included') {
		const perStore = price ? ` (worth ${formatAmount(price.amount / 100, price.currency)}/mo per location).` : '.';
		return {
			id: `channel-${type}`,
			icon: ShoppingBag,
			title,
			description: `Free with Pro — using one of your allowance slots${perStore}`,
			right: <Badge variant="secondary">Free with Pro</Badge>
		};
	}

	const priceLabel = price
		? `${formatAmount(price.amount / 100, price.currency)} per month, per location.`
		: 'Billed alongside your base plan.';
	return {
		id: `channel-${type}`,
		icon: ShoppingBag,
		title,
		description: bundlePeers.length ? `${priceLabel} Bundled with ${bundlePeers.map(humaniseChannel).join(', ')}.` : priceLabel,
		right: <Badge variant="secondary">Add-on</Badge>
	};
}

// The commercetools bundle contains PR + LC; render as "Reservations & Local Checkout"
// when both are enabled, otherwise fall back to the humanised single type.
function preferredBundleDisplay(type, peers) {
	const all = [type, ...peers].sort();
	if (all.includes('product-reservations') && all.includes('local-checkout')) return 'reservations & local-checkout';
	if (all.includes('meta-commerce') && all.includes('meta-commerce-feed')) return 'meta-commerce';
	return type;
}

function humaniseChannel(type) {
	return type.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function AddOnRow({ item }) {
	const Icon = item.icon;
	return (
		<li className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary-soft-foreground">
				<Icon className="h-5 w-5" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold text-foreground">{item.title}</div>
				<div className="text-xs text-muted-foreground">{item.description}</div>
			</div>
			{item.right}
		</li>
	);
}
